import {
  parseISO,
  add,
  differenceInDays,
  startOfToday,
  format,
  DATE_TIME_FORMATS,
} from '@white-label-helper/date-utilities';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import type { LoungesSearchCriteria } from '@white-label-types/product-types';

type GaDataLayer = {
  /** Whether the user is logged in or not, we should be able to get this from auth0 this.$auth.isAuthenticated */
  loginStatus: boolean;
  /** The user's ID. There isn't a clear ID with auth0 but the user.sub is the closest atm this.$auth.user.sub */
  userId: string | undefined;
  /** The airport name */
  airportName: string | undefined;
  /** The airport code */
  airportCode: string | undefined;
  /** The airport terminal number or "dontKnow" */
  airportTerminalId?: string;
  /** What the service is (e.g. "parking") */
  serviceType: string;
  /** How far in advance the booking was made */
  daysAheadOfBooking: number | undefined;
  /** The date of departure */
  parkingDepartureDate?: string;
  /** The time of departure */
  parkingDepartureTime?: string;
  /** The date of return */
  parkingReturnDate?: string;
  /** The time of return */
  parkingReturnTime?: string;
  /** The duration between departure and return */
  stayDuration?: number;
  /** The order reference */
  orderReference?: string;
  /** The coupon or voucher code */
  couponVoucher?: string;
  /** The name of the partner */
  partnerName: string | undefined;
  /** The language of the site */
  siteLanguage: string;
  /** The parking name */
  carParkName?: string;
  /** The Lounge Departure date */
  loungeDepartureDate?: string;
  /** The Lounge Departure time */
  loungeDepartureTime?: string;
  /** The Lounge Return date */
  loungeReturnDate?: string;
  /** The Lounge Return time */
  loungeReturnTime?: string;
  /** The FastTrack Departure date */
  fastTrackDepartureDate?: string;
  /** The FastTrack Departure time */
  fastTrackDepartureTime?: string;
  /** The FastTrack Return date */
  fastTrackReturnDate?: string;
  /** The FastTrack Return time */
  fastTrackReturnTime?: string;
  /** The Airline code */
  airLine: string | undefined;
  /** Terminal ID */
  terminalId: string | undefined;
  /** The customers chosen departure date */
  flightDepartureDate: string | undefined;
  /** The customers chosen flight departure time */
  flightDepartureTime: string | undefined;
  /** Baggage type */
  baggage?: string;
  /** Requested for tracking but currently not sure what to use */
  userType: string | undefined;
  /** Lounge Name, attached if the product has one */
  loungeName?: string | undefined;
  /** FastTrack Name, attached if the product has one */
  fastTrackName?: string | undefined;
  /** FastTrack journey type, can be 'oneWay' or 'twoWay' */
  journeyType?: string | undefined;
  /** Lounge entry date */
  entryDate: string | undefined;
  /** Lounge entry time */
  entryTime: string | undefined;
  /** Number of guests */
  numberOfGuest: number | undefined;
  /** Number of adults and seniors */
  adult?: number;
  /** Number of Children and Infants */
  children?: number;
  /** GA event name */
  event?: string;
  /** The users selected timeslot */
  timeslot: string | undefined;
};

/** The data object to send to GA */
const gaLayer: GaDataLayer = {
  loginStatus: false,
  userId: undefined,
  airportName: undefined,
  airportCode: undefined,
  airportTerminalId: undefined,
  serviceType: 'parking',
  daysAheadOfBooking: 0,
  parkingDepartureDate: undefined,
  parkingDepartureTime: undefined,
  parkingReturnDate: undefined,
  parkingReturnTime: undefined,
  stayDuration: 0,
  couponVoucher: undefined,
  partnerName: undefined,
  siteLanguage: 'en-US',
  carParkName: undefined,
  event: 'globalParameters',
};

type Options = {
  loginStatus?: boolean;
  userId: number | undefined;
  airportName: string | undefined;
  airportCode: string | undefined;
  airportTerminalId: string | undefined;
  serviceType?: string;
  daysAheadOfBooking?: number;
  parkingDepartureDate: string | undefined;
  parkingDepartureTime: string | undefined;
  parkingReturnDate: string | undefined;
  parkingReturnTime?: string;
  stayDuration?: number;
  orderReference: string | undefined;
  couponVoucher: string | undefined;
  partnerName: string | undefined;
  siteLanguage?: string;
  carParkName: string | undefined;
  event?: string;
};

/**
 * Update the `gaLayer` object with new options.
 * Persists existing values if a new value is not provided.
 * Intended to be used with `gtm.push` to update the "All Pages DataLayer" in GTM.
 * @param options - The options to update the dataLayer with - can be a subset of possible options
 * @returns The updated dataLayer object
 */

export const gaDataLayer = (options: Partial<Options>) => {
  const {
    loginStatus = gaLayer.loginStatus,
    airportName = gaLayer.airportName,
    airportCode = gaLayer.airportCode,
    airportTerminalId = gaLayer.airportTerminalId,
    parkingDepartureDate = gaLayer.parkingDepartureDate,
    parkingDepartureTime = gaLayer.parkingDepartureTime,
    parkingReturnDate = gaLayer.parkingReturnDate,
    parkingReturnTime = gaLayer.parkingReturnTime,
    couponVoucher = gaLayer.couponVoucher,
    partnerName = gaLayer.partnerName,
    siteLanguage = gaLayer.siteLanguage,
    carParkName = gaLayer.carParkName,
    ...remainingOptions
  } = options;

  if (parkingDepartureDate) {
    gaLayer.daysAheadOfBooking = differenceInDays(
      parseISO(parkingDepartureDate),
      startOfToday()
    );
  }

  if (parkingDepartureDate && parkingReturnDate) {
    const arrival = parseISO(parkingReturnDate);
    const departure = parseISO(parkingDepartureDate);
    const addOneDay = add(arrival, { days: 1 });
    // Adding 1 day to arrival to make sure it's included in the calculation
    const days = (+addOneDay - +departure) / 864e5; // Days in milliseconds
    // The min value is 1
    gaLayer.stayDuration = Math.round(days) || 1;
  }

  if (options.event) {
    gaLayer.event = options.event;
  }

  if (options.orderReference) {
    gaLayer.orderReference = options.orderReference;
  }

  gaLayer.loginStatus = loginStatus;
  gaLayer.airportCode = airportCode;
  gaLayer.airportTerminalId = airportTerminalId;
  gaLayer.parkingDepartureDate = parkingDepartureDate;
  gaLayer.parkingDepartureTime = parkingDepartureTime;
  gaLayer.parkingReturnDate = parkingReturnDate;
  gaLayer.parkingReturnTime = parkingReturnTime;
  gaLayer.couponVoucher = couponVoucher;
  gaLayer.partnerName = partnerName;
  gaLayer.siteLanguage = siteLanguage;
  gaLayer.carParkName = carParkName;
  gaLayer.airportName = airportName;

  Object.assign(gaLayer, remainingOptions);

  return gaLayer;
};

export function gaDataLayerLounges(
  formData: LoungesSearchCriteria['lounges'],
  extend = {}
) {
  const groups = formData.groups || {};
  const numberOfGuest = Object.values(groups)
    .filter<number>((value): value is number => typeof value === 'number')
    .reduce((acc, value) => acc + value, 0);

  const primaryProductType = getAppVariable('primary_product_type');
  const entryDate = formData.date1 || formData.date || '';

  const data: Partial<GaDataLayer> = {
    airportCode: getAppVariable('poi.code'),
    terminalId: `${formData?.terminal || 'dontKnow'}`,
    airLine: formData?.airline || undefined,
    baggage: formData?.baggage_type || undefined,
    userType: undefined,
    partnerName: getAppVariable('partner_name'),
    daysAheadOfBooking: differenceInDays(parseISO(entryDate), startOfToday()),
    entryDate: entryDate
      ? format(parseISO(entryDate), DATE_TIME_FORMATS.year_month_day)
      : undefined,
    entryTime: formData.time1 || formData.time || undefined,
    numberOfGuest,
    adult: (groups['adult'] || 0) + (groups['senior'] || 0) || undefined,
    children: (groups['child'] || 0) + (groups['infant'] || 0) || undefined,
    loginStatus: false,
    userId: undefined,
    siteLanguage: getAppVariable('default_language'),
    event: 'globalParameters',
    serviceType: primaryProductType,
    ...extend,
  };

  if (primaryProductType === 'fasttrack') {
    data.fastTrackDepartureDate = formData?.date || formData?.date1 || formData.entryDate || undefined;
    data.fastTrackDepartureTime = formData?.time || formData?.time1 || formData.entryTime?.value || undefined;
    data.fastTrackReturnDate = formData?.date2 || formData.entryDate || undefined;
    data.fastTrackReturnTime = formData?.time2 || formData.entryTime?.value || undefined;
    data.fastTrackName = extend.fastTrackName || undefined;

    if (data.fastTrackDepartureDate && data.fastTrackReturnDate) {
      data.journeyType = 'flyingInAndOut';
    } else if (data.fastTrackDepartureDate) {
      data.journeyType = 'flyingOut';
    } else if (data.fastTrackReturnDate) {
      data.journeyType = 'flyingIn';
    } else {
      data.journeyType = undefined;
    }
  } else {
    data.loungeDepartureDate = formData?.date || formData.entryDate || undefined;
    data.loungeDepartureTime = formData?.time || formData.entryTime?.value || undefined;
    data.loungeReturnDate = formData?.date2 || formData.entryDate || undefined;
    data.loungeReturnTime = formData?.time2 || formData.entryTime?.value || undefined;
    data.loungeName = extend.loungeName || undefined;
  }
  return data;
}

/**
 * Values that are used at all steps of the journey
 * @returns The dataLayer object
 */
export const gaDataLayerTimeslotCommonValues: Partial<GaDataLayer> = {
  airportCode: '',
  terminalId: undefined,
  airLine: undefined,
  userType: undefined,
  partnerName: '',
  serviceType: 'securityFastTrack',
  daysAheadOfBooking: undefined,
  numberOfGuest: undefined,
  loginStatus: false,
  userId: undefined,
};

/**
 * Values that are used only in step 1 of the journey
 * @returns The dataLayer object
 */
export const gaDataLayerTimeslotStep1Values: Partial<GaDataLayer> = {
  siteLanguage: '',
  timeslot: undefined,
  flightDepartureDate: undefined,
  flightDepartureTime: undefined,
};

/**
 * Values that are used only in step 2 of the journey
 * @returns The dataLayer object
 */
export const gaDataLayerTimeslotStep2Values: Partial<GaDataLayer> = {
  loungeName: undefined,
  entryDate: undefined,
  entryTime: undefined,
  adult: 0,
  children: 0,
};

export const gaDataLayerTimeslotStep1InitialValues: GaDataLayer = { ...gaDataLayerTimeslotCommonValues, ...gaDataLayerTimeslotStep1Values } as GaDataLayer;
export const gaDataLayerTimeslotStep2InitialValues: GaDataLayer = { ...gaDataLayerTimeslotCommonValues, ...gaDataLayerTimeslotStep2Values } as GaDataLayer;

/**
 * Create the data layer object in step 1 of the journey
 * @param formData Values that are subject to change at this stage of the journey
 * @returns The dataLayer object
 */
export function gaDataLayerTimeslotHome(
  formData: {
    terminal?: string;
    numberOfGuest?: number;
    flightDepartureDate: string | undefined;
    airline?: string;
    flightDepartureTime?: string;
  },
): GaDataLayer {
  const result = gaDataLayerTimeslotStep1InitialValues;

  if (formData.flightDepartureDate) {
    result.daysAheadOfBooking = differenceInDays(parseISO(formData.flightDepartureDate), startOfToday());
  }
  result.terminalId = formData.terminal ?? result.terminalId;
  result.numberOfGuest = formData.numberOfGuest ?? result.numberOfGuest;
  result.flightDepartureDate = formData.flightDepartureDate ?? result.flightDepartureDate;
  result.airLine = formData.airline ?? result.airLine;
  result.flightDepartureTime = formData.flightDepartureTime ?? result.flightDepartureTime;
  result.airportCode = getAppVariable('poi.code');
  result.partnerName = getAppVariable('partner_name');
  result.siteLanguage = getAppVariable('default_language');

  return result;
};

// For step 2
export function gaDataLayerTimeslot(
  formData: {
    terminal?: string;
    passengers?: number;
    departureDate: string | undefined;
    airline?: string;
    flightDepartureTime?: string;
  },
): GaDataLayer {
  const result = gaDataLayerTimeslotStep2InitialValues;

  if (formData.departureDate) {
    result.daysAheadOfBooking = differenceInDays(parseISO(formData.departureDate), startOfToday());
  }

  result.terminalId = formData.terminal ?? result.terminalId;
  result.numberOfGuest = formData.passengers ?? result.numberOfGuest;
  result.entryDate = formData.departureDate ?? result.entryDate;
  result.airLine = formData.airline ?? result.airLine;
  result.flightDepartureTime = formData.flightDepartureTime ?? result.flightDepartureTime;
  result.adult = formData.passengers ?? result.adult;
  result.airportCode = getAppVariable('poi.code');
  result.partnerName = getAppVariable('partner_name');

  return result;
}
